import { useEffect, useRef } from 'react';
import {
  registerQuickSaleSettingsApi,
  QuickSaleSettingsApi,
  QuickSaleSettingsFields,
  QuickSalePaymentOptionInput,
} from '../../../aichat/agent/uiBridge';
import { flattenTemplateSelection, type WarehouseStockTemplate } from '../utils/warehouseStockTemplates';
import { OTHER_PAYMENT_OPTIONS } from '../settings/PaymentSettingsTab';

// ============================================================================
// AI AJAN KÖPRÜSÜ — Hızlı Satış Ayarlar Modalı (QuickSaleScreenSettingsModal)
// Modal açıkken imperatif bir API kaydeder: sekme değiştirme, ayar özeti,
// whitelist'li alan değiştirme, kaydetme, kapatma. Ajan alanları FORMDA
// değiştirir; kullanıcı değişikliği ekranda görür, kayıt save() ile yapılır.
// Desen: useInvoiceAgentBridge (cfgRef → stale closure koruması).
// ============================================================================

interface PaymentOptionLike {
  id: string;
  label: string;
  enabled: boolean;
  /** cash | pos | openAccount | other | online ... (modal PaymentOption.type) */
  type?: string;
}

interface NamedItem {
  id: string;
  name: string;
}

/** Ödeme bağlama listeleri — modal lazy yükledikten sonra döner. */
export interface PaymentBindingData {
  cashboxes: NamedItem[];
  bankAccounts: Array<{
    id: string;
    bankName: string;
    isPosAccount?: boolean;
    subAccounts?: Record<string, { id: string; accountName: string; isPosAccount?: boolean }>;
  }>;
}

export interface QuickSaleSettingsAgentBridgeConfig {
  /** Modal açık ve kayıt için hazır mı? false iken API kaydedilmez. */
  enabled: boolean;
  screenName: string;
  isRestaurantMode: boolean;
  isPackageMode: boolean;

  // Sekmeler
  tabs: Array<{ value: number; label: string }>;
  activeTab: number;
  setActiveTab: (value: number) => void;

  // Ödeme Yöntemleri
  paymentOptions: PaymentOptionLike[];
  setPaymentOptionEnabled: (id: string, enabled: boolean) => void;
  /** Kasa + banka listelerini (boşsa) yükler ve döner — ödeme yöntemi bağlamak için. */
  ensurePaymentBindingData: () => Promise<PaymentBindingData>;
  /** Yeni ödeme yöntemini alanlarıyla birlikte form state'ine ekler; eklenen kaydı döner. */
  addPaymentOptionWithFields: (
    type: 'cash' | 'pos' | 'openAccount' | 'other',
    fields: Record<string, any>
  ) => { id: string; label: string };
  /** Ödeme yöntemini form state'inden kaldırır (modal handleDeletePaymentOption). */
  removePaymentOptionById: (id: string) => void;

  // Personel Atama
  allPersonnel: NamedItem[];
  selectedPersonnelIds: string[];
  setSelectedPersonnelIds: (ids: string[]) => void;

  // Mutfak Entegrasyonu (Ek Ayarlar: useKitchenScreen + zorunlu QR menü seçimi)
  selectedQrMenuName: string | null;
  /** QR menü tanımlarını getirir (menu-definitions ucu). */
  listQrMenus: () => Promise<Array<{ id: string; name: string; branchName?: string }>>;
  /** QR menü seçimini forma yazar (null = temizle). */
  setKitchenQrMenu: (menu: { id: string; name: string } | null) => void;

  // Kasiyer
  useCashierSystem: boolean;
  setUseCashierSystem: (v: boolean) => void;
  autoLockEnabled: boolean;
  setAutoLockEnabled: (v: boolean) => void;
  autoLockTimeoutSeconds: number;
  setAutoLockTimeoutSeconds: (v: number) => void;

  // Depo/Stok
  allWarehouses: NamedItem[];
  selectedWarehouseIds: string[];
  setSelectedWarehouseIds: (ids: string[]) => void;
  preventNegativeStock: boolean;
  setPreventNegativeStock: (v: boolean) => void;

  // Depo/Stok taslakları (şirket geneli, localStorage — yalnızca aynı tarayıcıda görünür)
  listWarehouseTemplates: () => WarehouseStockTemplate[];
  /**
   * Taslağın kategori/ürünlerini hedef deponun altına uygular (kaydetmez) —
   * modal onApplyWarehouseTemplate ile aynı akış. Taslağın kendi deposu
   * kullanılmadığı için hedef depo zorunludur.
   */
  applyWarehouseTemplate: (template: WarehouseStockTemplate, targetWarehouseId: string) => void;
  /** Formdaki mevcut depo/stok seçimini adla kaydeder; aynı ad varsa günceller. */
  saveWarehouseTemplate: (name: string) => WarehouseStockTemplate | null;

  // Yazdırma
  printingMode: 'single' | 'category';
  setPrintingMode: (v: 'single' | 'category') => void;
  availablePrinters: NamedItem[];
  defaultReceiptPrinterName: string | null;
  setDefaultReceiptPrinterByName: (printer: NamedItem) => void;

  // Ek Ayarlar
  prepaidSystemEnabled: boolean;
  setPrepaidSystemEnabled: (v: boolean) => void;
  showProductImages: boolean;
  setShowProductImages: (v: boolean) => void;
  showCashChangeModal: boolean;
  setShowCashChangeModal: (v: boolean) => void;
  allowAccountCreationOnSale: boolean;
  setAllowAccountCreationOnSale: (v: boolean) => void;
  useKitchenScreen: boolean;
  setUseKitchenScreen: (v: boolean) => void;
  showScreenLayoutButton: boolean;
  setShowScreenLayoutButton: (v: boolean) => void;
  warnIfFiscalNotConnected: boolean;
  setWarnIfFiscalNotConnected: (v: boolean) => void;
  useFiscalDeviceForCash: boolean;
  setUseFiscalDeviceForCash: (v: boolean) => void;

  // Yerel Servis
  localServiceUrl: string;
  setLocalServiceUrl: (v: string) => void;

  // Kayıt / kapatma
  hasChanges: boolean;
  saving: boolean;
  error: string | null;
  save: () => Promise<void>;
  close: () => void;
}

/** TR-toleranslı ad karşılaştırması için normalize eder. */
const normalizeName = (value: string): string =>
  value
    .toLocaleLowerCase('tr-TR')
    .replace(/[çÇ]/g, 'c')
    .replace(/[ğĞ]/g, 'g')
    .replace(/[ıİiI]/g, 'i')
    .replace(/[öÖ]/g, 'o')
    .replace(/[şŞ]/g, 's')
    .replace(/[üÜ]/g, 'u')
    .trim();

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const useQuickSaleSettingsAgentBridge = (
  config: QuickSaleSettingsAgentBridgeConfig
): void => {
  // Kayıtlı API sabit kalır; en güncel state/handler'lara ref üzerinden erişilir.
  const cfgRef = useRef(config);
  cfgRef.current = config;

  const { enabled } = config;

  useEffect(() => {
    if (!enabled) return;

    const api: QuickSaleSettingsApi = {
      get screenName() {
        return cfgRef.current.screenName;
      },

      getTabs: () => cfgRef.current.tabs.map((t) => t.label),

      setTab: (tabName: string) => {
        const cfg = cfgRef.current;
        const wanted = normalizeName(tabName);
        let found = cfg.tabs.find((t) => normalizeName(t.label) === wanted);
        if (!found) {
          const partial = cfg.tabs.filter((t) => normalizeName(t.label).includes(wanted));
          if (partial.length === 1) found = partial[0];
        }
        if (!found) {
          return {
            success: false,
            error: `"${tabName}" adında sekme yok.`,
            availableTabs: cfg.tabs.map((t) => t.label),
          };
        }
        cfg.setActiveTab(found.value);
        return { success: true, activeTab: found.label };
      },

      getSummary: () => {
        const cfg = cfgRef.current;
        const activeTabLabel =
          cfg.tabs.find((t) => t.value === cfg.activeTab)?.label ?? String(cfg.activeTab);
        return {
          screenName: cfg.screenName,
          mode: cfg.isRestaurantMode ? 'restoran' : cfg.isPackageMode ? 'paket' : 'normal',
          activeTab: activeTabLabel,
          tabs: cfg.tabs.map((t) => t.label),
          hasUnsavedChanges: cfg.hasChanges,
          paymentOptions: cfg.paymentOptions.map((p) => ({
            label: p.label,
            enabled: p.enabled,
          })),
          useCashierSystem: cfg.useCashierSystem,
          autoLockEnabled: cfg.autoLockEnabled,
          autoLockTimeoutSeconds: cfg.autoLockTimeoutSeconds,
          selectedWarehouses: cfg.selectedWarehouseIds.map(
            (id) => cfg.allWarehouses.find((w) => w.id === id)?.name ?? id
          ),
          preventNegativeStock: cfg.preventNegativeStock,
          printingMode: cfg.printingMode,
          defaultReceiptPrinter: cfg.defaultReceiptPrinterName,
          prepaidSystemEnabled: cfg.prepaidSystemEnabled,
          showProductImages: cfg.showProductImages,
          showCashChangeModal: cfg.showCashChangeModal,
          allowAccountCreationOnSale: cfg.allowAccountCreationOnSale,
          useKitchenScreen: cfg.useKitchenScreen,
          showScreenLayoutButton: cfg.showScreenLayoutButton,
          warnIfFiscalNotConnected: cfg.warnIfFiscalNotConnected,
          useFiscalDeviceForCash: cfg.useFiscalDeviceForCash,
          localServiceUrl: cfg.localServiceUrl,
          assignedPersonnel: cfg.selectedPersonnelIds.map(
            (id) => cfg.allPersonnel.find((p) => p.id === id)?.name ?? id
          ),
          kitchenQrMenu: cfg.selectedQrMenuName || null,
        };
      },

      setFields: (fields: QuickSaleSettingsFields) => {
        const cfg = cfgRef.current;
        const applied: string[] = [];
        const errors: string[] = [];

        const applyBool = (key: string, value: unknown, setter: (v: boolean) => void) => {
          if (typeof value !== 'boolean') {
            errors.push(`${key}: true/false bekleniyor.`);
            return;
          }
          setter(value);
          applied.push(key);
        };

        for (const [key, value] of Object.entries(fields)) {
          if (value === undefined || value === null) continue;
          switch (key as keyof QuickSaleSettingsFields) {
            case 'paymentOptions': {
              if (typeof value !== 'object' || Array.isArray(value)) {
                errors.push('paymentOptions: { "Nakit": true } biçiminde nesne bekleniyor.');
                break;
              }
              for (const [label, enabledVal] of Object.entries(value as Record<string, boolean>)) {
                const wanted = normalizeName(label);
                const option =
                  cfg.paymentOptions.find((p) => normalizeName(p.label) === wanted) ||
                  cfg.paymentOptions.find((p) => normalizeName(p.label).includes(wanted));
                if (!option) {
                  errors.push(
                    `paymentOptions: "${label}" bulunamadı. Mevcut: ${cfg.paymentOptions
                      .map((p) => p.label)
                      .join(', ')}`
                  );
                  continue;
                }
                cfg.setPaymentOptionEnabled(option.id, enabledVal === true);
                applied.push(`paymentOptions.${option.label}=${enabledVal === true}`);
              }
              break;
            }
            case 'useCashierSystem':
              applyBool(key, value, cfg.setUseCashierSystem);
              break;
            case 'autoLockEnabled':
              applyBool(key, value, cfg.setAutoLockEnabled);
              break;
            case 'autoLockTimeoutSeconds': {
              const num = Number(value);
              if (!Number.isFinite(num) || num <= 0) {
                errors.push('autoLockTimeoutSeconds: pozitif sayı bekleniyor.');
                break;
              }
              cfg.setAutoLockTimeoutSeconds(num);
              applied.push(key);
              break;
            }
            case 'selectedWarehouses': {
              if (!Array.isArray(value)) {
                errors.push('selectedWarehouses: depo adı/id dizisi bekleniyor.');
                break;
              }
              const resolved: string[] = [];
              let failed = false;
              for (const entry of value as string[]) {
                const wanted = normalizeName(String(entry));
                const wh =
                  cfg.allWarehouses.find((w) => w.id === String(entry)) ||
                  cfg.allWarehouses.find((w) => normalizeName(w.name) === wanted) ||
                  cfg.allWarehouses.find((w) => normalizeName(w.name).includes(wanted));
                if (!wh) {
                  errors.push(
                    `selectedWarehouses: "${entry}" bulunamadı. Mevcut depolar: ${cfg.allWarehouses
                      .map((w) => w.name)
                      .join(', ')}`
                  );
                  failed = true;
                  break;
                }
                if (!resolved.includes(wh.id)) resolved.push(wh.id);
              }
              if (!failed) {
                cfg.setSelectedWarehouseIds(resolved);
                applied.push(key);
              }
              break;
            }
            case 'preventNegativeStock':
              applyBool(key, value, cfg.setPreventNegativeStock);
              break;
            case 'printingMode': {
              const mode = String(value).toLowerCase();
              if (mode !== 'single' && mode !== 'category') {
                errors.push("printingMode: 'single' veya 'category' bekleniyor.");
                break;
              }
              cfg.setPrintingMode(mode);
              applied.push(key);
              break;
            }
            case 'defaultReceiptPrinter': {
              const wanted = normalizeName(String(value));
              const printer =
                cfg.availablePrinters.find((p) => normalizeName(p.name) === wanted) ||
                cfg.availablePrinters.find((p) => normalizeName(p.name).includes(wanted));
              if (!printer) {
                errors.push(
                  `defaultReceiptPrinter: "${value}" bulunamadı. Mevcut yazıcılar: ${
                    cfg.availablePrinters.map((p) => p.name).join(', ') || '(yok)'
                  }`
                );
                break;
              }
              cfg.setDefaultReceiptPrinterByName(printer);
              applied.push(key);
              break;
            }
            case 'prepaidSystemEnabled':
              applyBool(key, value, cfg.setPrepaidSystemEnabled);
              break;
            case 'showProductImages':
              applyBool(key, value, cfg.setShowProductImages);
              break;
            case 'showCashChangeModal':
              applyBool(key, value, cfg.setShowCashChangeModal);
              break;
            case 'allowAccountCreationOnSale':
              applyBool(key, value, cfg.setAllowAccountCreationOnSale);
              break;
            case 'useKitchenScreen':
              applyBool(key, value, cfg.setUseKitchenScreen);
              break;
            case 'showScreenLayoutButton':
              applyBool(key, value, cfg.setShowScreenLayoutButton);
              break;
            case 'warnIfFiscalNotConnected':
              applyBool(key, value, cfg.setWarnIfFiscalNotConnected);
              break;
            case 'useFiscalDeviceForCash':
              applyBool(key, value, cfg.setUseFiscalDeviceForCash);
              break;
            case 'localServiceUrl': {
              if (typeof value !== 'string' || !value.trim()) {
                errors.push('localServiceUrl: boş olmayan bir URL bekleniyor.');
                break;
              }
              cfg.setLocalServiceUrl(value.trim());
              applied.push(key);
              break;
            }
            default:
              errors.push(`Bilinmeyen alan: ${key}`);
          }
        }

        return {
          success: errors.length === 0,
          appliedFields: applied,
          ...(errors.length > 0 ? { errors } : {}),
          note: 'Değişiklikler formda yapıldı, henüz KAYDEDİLMEDİ — kaydetmek için saveQuickSaleSettings kullan.',
        };
      },

      listPaymentBindings: async () => {
        const cfg = cfgRef.current;
        const { cashboxes, bankAccounts } = await cfg.ensurePaymentBindingData();
        const posAccounts: string[] = [];
        for (const bank of bankAccounts) {
          if (bank.isPosAccount) posAccounts.push(bank.bankName);
          for (const sub of Object.values(bank.subAccounts ?? {})) {
            if (sub.isPosAccount) posAccounts.push(`${bank.bankName} / ${sub.accountName}`);
          }
        }
        return {
          success: true,
          cashboxes: cashboxes.map((c) => c.name),
          posAccounts,
          otherPaymentLabels: OTHER_PAYMENT_OPTIONS,
          currentPaymentOptions: cfg.paymentOptions.map((p) => ({
            label: p.label,
            type: p.type ?? null,
            enabled: p.enabled,
          })),
        };
      },

      addPaymentOption: async (input: QuickSalePaymentOptionInput) => {
        const cfg = cfgRef.current;
        const type = input?.type;
        if (!type || !['cash', 'pos', 'openAccount', 'other'].includes(type)) {
          return {
            success: false,
            error: "type: 'cash' | 'pos' | 'openAccount' | 'other' olmalı.",
          };
        }
        const { cashboxes, bankAccounts } = await cfg.ensurePaymentBindingData();
        const fields: Record<string, any> = {};
        let label = (input.label || '').trim();

        // cash ve other tipleri kasaya bağlanır
        if (type === 'cash' || type === 'other') {
          const cashboxName = (input.cashboxName || '').trim();
          if (!cashboxName) {
            return {
              success: false,
              error: `'${type}' tipi ödeme için cashboxName zorunlu (tahsilat kasası).`,
              availableCashboxes: cashboxes.map((c) => c.name),
            };
          }
          const wanted = normalizeName(cashboxName);
          const cashbox =
            cashboxes.find((c) => normalizeName(c.name) === wanted) ||
            cashboxes.find((c) => normalizeName(c.name).includes(wanted));
          if (!cashbox) {
            return {
              success: false,
              error: `"${cashboxName}" adında kasa bulunamadı.`,
              availableCashboxes: cashboxes.map((c) => c.name),
            };
          }
          fields.cashboxId = cashbox.id;
          fields.cashboxName = cashbox.name;
        }

        if (type === 'cash') label = label || 'Nakit';
        if (type === 'openAccount') label = label || 'Açık Hesap';
        if (type === 'other') {
          if (!label) {
            return {
              success: false,
              error: "'other' tipi için label zorunlu (sabit listeden).",
              validLabels: OTHER_PAYMENT_OPTIONS,
            };
          }
          const wanted = normalizeName(label);
          const validLabel =
            OTHER_PAYMENT_OPTIONS.find((o) => normalizeName(o) === wanted) ||
            OTHER_PAYMENT_OPTIONS.find((o) => normalizeName(o).includes(wanted));
          if (!validLabel) {
            return {
              success: false,
              error: `"${label}" geçerli bir diğer ödeme yöntemi değil.`,
              validLabels: OTHER_PAYMENT_OPTIONS,
            };
          }
          label = validLabel;
        }

        if (type === 'pos') {
          const accountName = (input.bankAccountName || '').trim();
          const listPosNames = () => {
            const names: string[] = [];
            for (const bank of bankAccounts) {
              if (bank.isPosAccount) names.push(bank.bankName);
              for (const sub of Object.values(bank.subAccounts ?? {})) {
                if (sub.isPosAccount) names.push(`${bank.bankName} / ${sub.accountName}`);
              }
            }
            return names;
          };
          if (!accountName) {
            return {
              success: false,
              error: "'pos' tipi ödeme için bankAccountName zorunlu.",
              availablePosAccounts: listPosNames(),
            };
          }
          const wanted = normalizeName(accountName);
          let matched: { bankId: string; subId: string | null; name: string } | null = null;
          for (const bank of bankAccounts) {
            if (
              bank.isPosAccount &&
              (normalizeName(bank.bankName) === wanted || normalizeName(bank.bankName).includes(wanted))
            ) {
              matched = { bankId: bank.id, subId: null, name: bank.bankName };
              break;
            }
            for (const sub of Object.values(bank.subAccounts ?? {})) {
              if (!sub.isPosAccount) continue;
              const fullName = `${bank.bankName} / ${sub.accountName}`;
              const candidates = [sub.accountName, fullName].map(normalizeName);
              if (candidates.some((n) => n === wanted || n.includes(wanted))) {
                matched = { bankId: bank.id, subId: sub.id, name: fullName };
                break;
              }
            }
            if (matched) break;
          }
          if (!matched) {
            return {
              success: false,
              error: `"${accountName}" adında POS hesabı bulunamadı.`,
              availablePosAccounts: listPosNames(),
            };
          }
          fields.bankAccountId = matched.bankId;
          fields.subAccountId = matched.subId;
          fields.bankAccountName = matched.name;
          label = label || 'Yeni POS';
        }

        const duplicate = cfg.paymentOptions.find(
          (p) => normalizeName(p.label) === normalizeName(label)
        );
        if (duplicate) {
          return {
            success: false,
            error: `"${duplicate.label}" adlı ödeme yöntemi zaten ekli — önce removePaymentOption ile kaldır veya farklı ad ver.`,
          };
        }

        const created = cfg.addPaymentOptionWithFields(type, { ...fields, label, enabled: true });
        return {
          success: true,
          added: { label: created.label, type },
          note: 'Ödeme yöntemi formda eklendi, henüz KAYDEDİLMEDİ — kaydetmek için saveQuickSaleSettings kullan.',
        };
      },

      removePaymentOption: (label: string) => {
        const cfg = cfgRef.current;
        const wanted = normalizeName((label || '').trim());
        if (!wanted) return { success: false, error: 'label zorunlu.' };
        let found = cfg.paymentOptions.find((p) => normalizeName(p.label) === wanted);
        if (!found) {
          const partial = cfg.paymentOptions.filter((p) => normalizeName(p.label).includes(wanted));
          if (partial.length === 1) found = partial[0];
        }
        if (!found) {
          return {
            success: false,
            error: `"${label}" adında ödeme yöntemi yok.`,
            currentPaymentOptions: cfg.paymentOptions.map((p) => p.label),
          };
        }
        cfg.removePaymentOptionById(found.id);
        return {
          success: true,
          removed: found.label,
          note: 'Formdan kaldırıldı, henüz KAYDEDİLMEDİ — kaydetmek için saveQuickSaleSettings kullan.',
        };
      },

      listAssignablePersonnel: () => {
        const cfg = cfgRef.current;
        return {
          success: true,
          assigned: cfg.selectedPersonnelIds.map(
            (id) => cfg.allPersonnel.find((p) => p.id === id)?.name ?? id
          ),
          available: cfg.allPersonnel.map((p) => p.name),
        };
      },

      setAssignedPersonnel: (names: string[], mode: 'add' | 'remove' | 'replace') => {
        const cfg = cfgRef.current;
        if (!Array.isArray(names) || names.length === 0) {
          return { success: false, error: 'En az bir personel adı ver.' };
        }
        const resolvedIds: string[] = [];
        const errors: string[] = [];
        for (const name of names) {
          const wanted = normalizeName(String(name));
          const match =
            cfg.allPersonnel.find((p) => normalizeName(p.name) === wanted) ||
            cfg.allPersonnel.find((p) => normalizeName(p.name).includes(wanted));
          if (!match) {
            errors.push(`"${name}" bulunamadı.`);
            continue;
          }
          if (!resolvedIds.includes(match.id)) resolvedIds.push(match.id);
        }
        if (errors.length > 0) {
          return {
            success: false,
            errors,
            availablePersonnel: cfg.allPersonnel.map((p) => p.name),
          };
        }
        let next: string[];
        if (mode === 'replace') next = resolvedIds;
        else if (mode === 'remove')
          next = cfg.selectedPersonnelIds.filter((id) => !resolvedIds.includes(id));
        else
          next = [
            ...cfg.selectedPersonnelIds,
            ...resolvedIds.filter((id) => !cfg.selectedPersonnelIds.includes(id)),
          ];
        cfg.setSelectedPersonnelIds(next);
        return {
          success: true,
          assigned: next.map((id) => cfg.allPersonnel.find((p) => p.id === id)?.name ?? id),
          note: 'Atama formda yapıldı, henüz KAYDEDİLMEDİ — kaydetmek için saveQuickSaleSettings kullan.',
        };
      },

      setKitchenIntegration: async (enabled: boolean, qrMenuName?: string) => {
        const cfg = cfgRef.current;
        if (enabled !== true) {
          cfg.setUseKitchenScreen(false);
          cfg.setKitchenQrMenu(null);
          return {
            success: true,
            message:
              'Mutfak entegrasyonu kapatıldı (formda) — kaydetmek için saveQuickSaleSettings kullan.',
          };
        }
        const menus = await cfg.listQrMenus();
        const name = (qrMenuName || '').trim();
        if (!name) {
          return {
            success: false,
            error: 'Mutfak entegrasyonu aktifken QR menü seçimi zorunlu — qrMenuName ver.',
            availableQrMenus: menus.map((m) => m.name),
          };
        }
        const wanted = normalizeName(name);
        let menu = menus.find((m) => normalizeName(m.name) === wanted);
        if (!menu) {
          const partial = menus.filter((m) => normalizeName(m.name).includes(wanted));
          if (partial.length === 1) menu = partial[0];
        }
        if (!menu) {
          return {
            success: false,
            error: `"${name}" adında QR menü bulunamadı.`,
            availableQrMenus: menus.map((m) => m.name),
            note: 'Yeni QR menü createQrMenu eylemiyle oluşturulabilir.',
          };
        }
        cfg.setUseKitchenScreen(true);
        cfg.setKitchenQrMenu({ id: menu.id, name: menu.name });
        return {
          success: true,
          enabled: true,
          qrMenu: menu.name,
          note: 'Mutfak entegrasyonu formda ayarlandı, henüz KAYDEDİLMEDİ — kaydetmek için saveQuickSaleSettings kullan.',
        };
      },

      // Taslak artık depo taşımadığı için özet kategori/ürün sayısıyla veriliyor.
      listWarehouseTemplates: () =>
        cfgRef.current.listWarehouseTemplates().map((tpl) => {
          const selection = flattenTemplateSelection(tpl);
          return {
            name: tpl.name,
            categoryCount: selection.categoryIds.length,
            productCount: selection.productIds.length,
            updatedAt: new Date(tpl.updatedAt).toISOString(),
          };
        }),

      applyWarehouseTemplate: (name: string, warehouseName?: string) => {
        const cfg = cfgRef.current;
        const templates = cfg.listWarehouseTemplates();
        const wanted = normalizeName(name);
        let found = templates.find((tpl) => normalizeName(tpl.name) === wanted);
        if (!found) {
          const partial = templates.filter((tpl) => normalizeName(tpl.name).includes(wanted));
          if (partial.length === 1) found = partial[0];
        }
        if (!found) {
          return {
            success: false,
            error: `"${name}" adında depo/stok taslağı yok. Taslaklar tarayıcıda saklanır — bu bilgisayarda kayıtlı olmayabilir.`,
            availableTemplates: templates.map((tpl) => tpl.name),
          };
        }

        // Taslak kendi deposunu taşımıyor: hedef depo ya açıkça verilir ya da
        // ekranda tek depo seçiliyse o kullanılır. Belirsizse yanlış depoya
        // ürün yazmak yerine hata dönüyoruz.
        let targetWarehouseId = '';
        if (warehouseName) {
          const target = normalizeName(warehouseName);
          const warehouse =
            cfg.allWarehouses.find((w) => w.id === warehouseName) ||
            cfg.allWarehouses.find((w) => normalizeName(w.name) === target) ||
            cfg.allWarehouses.find((w) => normalizeName(w.name).includes(target));
          if (!warehouse) {
            return {
              success: false,
              error: `"${warehouseName}" adında depo bulunamadı.`,
              availableWarehouses: cfg.allWarehouses.map((w) => w.name),
            };
          }
          targetWarehouseId = warehouse.id;
        } else if (cfg.selectedWarehouseIds.length === 1) {
          targetWarehouseId = cfg.selectedWarehouseIds[0];
        } else {
          return {
            success: false,
            error:
              'Taslağın hangi depoya uygulanacağı belirsiz — warehouseName ver. Taslak, kaydedildiği ekranın deposunu taşımaz.',
            availableWarehouses: cfg.allWarehouses.map((w) => w.name),
            screenWarehouses: cfg.selectedWarehouseIds.map(
              (id) => cfg.allWarehouses.find((w) => w.id === id)?.name ?? id
            ),
          };
        }

        cfg.applyWarehouseTemplate(found, targetWarehouseId);
        const targetName =
          cfg.allWarehouses.find((w) => w.id === targetWarehouseId)?.name ?? targetWarehouseId;
        return {
          success: true,
          appliedTemplate: found.name,
          targetWarehouse: targetName,
          note:
            `Taslaktaki kategori/ürünler "${targetName}" deposuna eklendi (formda, henüz KAYDEDİLMEDİ — ` +
            'kaydetmek için saveQuickSaleSettings kullan). Ürünlerin bu depoda stok tanımı yoksa ilk ' +
            'satışta otomatik açılır ve stok eksiye düşer.',
        };
      },

      saveWarehouseTemplate: (name: string) => {
        const cleanName = (name || '').trim();
        if (!cleanName) return { success: false, error: 'Taslak adı zorunlu.' };
        const saved = cfgRef.current.saveWarehouseTemplate(cleanName);
        if (!saved) return { success: false, error: 'Taslak kaydedilemedi.' };
        const selection = flattenTemplateSelection(saved);
        return {
          success: true,
          savedTemplate: saved.name,
          categoryCount: selection.categoryIds.length,
          productCount: selection.productIds.length,
          note:
            'Taslak bu tarayıcıya kaydedildi — yalnızca bu bilgisayarda görünür. Uygulanırken hedef ' +
            'depo sorulur; kaynak ekranın deposu taşınmaz.',
        };
      },

      save: async () => {
        const before = cfgRef.current;
        if (before.saving) return { success: false, error: 'Kayıt zaten devam ediyor.' };
        await before.save();
        // handleSave hatayı state'e yazar (throw etmez); re-render sonrası oku.
        await sleep(150);
        const after = cfgRef.current;
        if (after.error) {
          return { success: false, error: after.error };
        }
        return {
          success: true,
          message: `"${before.screenName}" ekranının ayarları kaydedildi (modal kapandı).`,
        };
      },

      close: () => {
        const cfg = cfgRef.current;
        const hadChanges = cfg.hasChanges;
        cfg.close();
        return {
          success: true,
          message: hadChanges
            ? 'Modal kapatıldı — kaydedilmemiş değişiklikler vardı, kaydedilmedi.'
            : 'Modal kapatıldı.',
        };
      },
    };

    return registerQuickSaleSettingsApi(api);
  }, [enabled]);
};

export default useQuickSaleSettingsAgentBridge;
